import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DLQItem, Stage } from "../lusca-search-api/generated-src";
import { getAppSetting } from "../config/AppSettings";
import { DlqInfo } from "./useFetchDlqInfo";

interface BatchDlqResult {
  status: "PENDING" | "COMPLETED" | "ERROR";
  queryExecutionId?: string;
  dlqByOrder?: Record<
    string,
    {
      inDLQ: boolean;
      dlqItems: DLQItem[];
    }
  >;
}

const loadingDlqInfo: DlqInfo = { status: "loading", error: null };

/** One Athena query for every resolved order in a UAT Project. */
export default function useBatchDlqInfo(
  stage: Stage,
  transactionIds: string[]
): Record<string, DlqInfo> {
  const queryClient = useQueryClient();
  const orderIds = Array.from(new Set(transactionIds)).sort().join(",");
  const queryKey = ["dlqInfoBatch", stage, orderIds];

  const { data, error } = useQuery<BatchDlqResult, Error>(
    queryKey,
    async () => {
      const previous = queryClient.getQueryData<BatchDlqResult>(queryKey);
      const response = await axios.get(
        `https://${getAppSetting("apiUrl")}/jwt/dlq-info`,
        {
          params: {
            stage,
            orderIds,
            queryExecutionId: previous?.queryExecutionId,
          },
        }
      );
      return response.data;
    },
    {
      enabled: Boolean(stage && orderIds),
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: (result) =>
        result?.status === "PENDING" ? 1000 : false,
    }
  );

  return transactionIds.reduce<Record<string, DlqInfo>>(
    (byOrder, transactionId) => {
      if (error || data?.status === "ERROR") {
        byOrder[transactionId] = {
          status: "error",
          error: error ?? new Error("DLQ query failed"),
        };
      } else if (data?.status === "COMPLETED") {
        const result = data.dlqByOrder?.[transactionId];
        byOrder[transactionId] = {
          status: result?.inDLQ ? "inDlq" : "notInDlq",
          dlqItems: result?.dlqItems ?? [],
          error: null,
        };
      } else {
        byOrder[transactionId] = loadingDlqInfo;
      }
      return byOrder;
    },
    {}
  );
}
